import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { 
  Euro, CalendarDays, Truck, Trash2, RefreshCw, Search, 
  ChevronDown, ChevronUp, CheckCircle2, Clock, XCircle,
  AlertTriangle, Filter, Download, Users
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format, parseISO, isValid } from "date-fns";
import { fr } from "date-fns/locale";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GestionCommissionsPage() {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({
    total_fees: 0,
    total_paid: 0,
    total_pending: 0,
    total_exempt: 0,
    total_participants: 0,
    total_events: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedEvents, setExpandedEvents] = useState({});
  
  // Dialog states
  const [deleteDialog, setDeleteDialog] = useState({ open: false, participant: null });
  const [editDialog, setEditDialog] = useState({ open: false, participant: null, newAmount: "", newStatus: "" });

  useEffect(() => {
    loadFees();
  }, []);

  const loadFees = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/admin/participation-fees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data.events || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error("Error loading fees:", error);
      toast.error("Erreur lors du chargement des commissions");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = parseISO(dateStr);
      if (!isValid(date)) return dateStr;
      return format(date, "d MMM yyyy", { locale: fr });
    } catch {
      return dateStr;
    }
  };

  const toggleEventExpansion = (eventId) => {
    setExpandedEvents(prev => ({ ...prev, [eventId]: !prev[eventId] }));
  };

  const handleDelete = async () => {
    if (!deleteDialog.participant) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/admin/participation-fees/${deleteDialog.participant.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Commission supprimée");
      setDeleteDialog({ open: false, participant: null });
      loadFees();
    } catch (error) {
      console.error("Error deleting fee:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleUpdateStatus = async () => {
    if (!editDialog.participant || !editDialog.newStatus) return;
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_URL}/api/admin/participation-fees/${editDialog.participant.id}/status`, 
        { statut_paiement: editDialog.newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Statut mis à jour");
      setEditDialog({ open: false, participant: null, newAmount: "", newStatus: "" });
      loadFees();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleUpdateAmount = async () => {
    if (!editDialog.participant || editDialog.newAmount === "") return;
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_URL}/api/admin/participation-fees/${editDialog.participant.id}/amount`, 
        { montant_du: parseFloat(editDialog.newAmount) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Montant mis à jour");
      setEditDialog({ open: false, participant: null, newAmount: "", newStatus: "" });
      loadFees();
    } catch (error) {
      console.error("Error updating amount:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "paye":
        return <Badge className="bg-green-100 text-green-700 border-green-300"><CheckCircle2 className="w-3 h-3 mr-1" />Payé</Badge>;
      case "exempte":
        return <Badge className="bg-gray-100 text-gray-600 border-gray-300"><XCircle className="w-3 h-3 mr-1" />Exempté</Badge>;
      default:
        return <Badge className="bg-orange-100 text-orange-700 border-orange-300"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
    }
  };

  // Filter events based on search and status
  const filteredEvents = events.map(event => {
    const filteredParticipants = event.participants.filter(p => {
      const matchesSearch = !searchTerm || 
        p.food_truck_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.event_titre?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === "all" || p.statut_paiement === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
    
    return { ...event, participants: filteredParticipants };
  }).filter(event => event.participants.length > 0);

  return (
    <div className="space-y-6" data-testid="gestion-commissions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary flex items-center gap-2">
            <Euro className="w-7 h-7 text-primary" />
            Gestion des Commissions
          </h1>
          <p className="text-muted-foreground mt-1">
            Frais de participation aux événements (20€ par service)
          </p>
        </div>
        <Button onClick={loadFees} variant="outline" className="gap-2" disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-secondary">{stats.total_fees?.toFixed(0) || 0}€</div>
            <div className="text-xs text-muted-foreground">Total</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:ring-2 hover:ring-orange-300" onClick={() => setStatusFilter(statusFilter === 'en_attente' ? 'all' : 'en_attente')}>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-orange-600">{stats.total_pending?.toFixed(0) || 0}€</div>
            <div className="text-xs text-muted-foreground">En attente</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:ring-2 hover:ring-green-300" onClick={() => setStatusFilter(statusFilter === 'paye' ? 'all' : 'paye')}>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{stats.total_paid?.toFixed(0) || 0}€</div>
            <div className="text-xs text-muted-foreground">Payé</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:ring-2 hover:ring-gray-300" onClick={() => setStatusFilter(statusFilter === 'exempte' ? 'all' : 'exempte')}>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-gray-500">{stats.total_exempt?.toFixed(0) || 0}€</div>
            <div className="text-xs text-muted-foreground">Exempté</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-secondary">{stats.total_participants || 0}</div>
            <div className="text-xs text-muted-foreground">Participants</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-secondary">{stats.total_events || 0}</div>
            <div className="text-xs text-muted-foreground">Événements</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un food truck ou événement..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-48">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filtrer par statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="en_attente">En attente</SelectItem>
            <SelectItem value="paye">Payé</SelectItem>
            <SelectItem value="exempte">Exempté</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Events List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : filteredEvents.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Euro className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold text-secondary mb-2">Aucune commission</h3>
            <p className="text-muted-foreground">
              {searchTerm || statusFilter !== 'all' 
                ? "Aucun résultat pour ces critères" 
                : "Aucun frais de participation à afficher"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredEvents.map((event) => {
            const isExpanded = expandedEvents[event.event_id] !== false; // Default expanded
            const totalEvent = event.participants.reduce((sum, p) => sum + (p.montant_du || 0), 0);
            const pendingCount = event.participants.filter(p => p.statut_paiement === 'en_attente').length;
            
            return (
              <Card key={event.event_id} className="overflow-hidden">
                <Collapsible open={isExpanded} onOpenChange={() => toggleEventExpansion(event.event_id)}>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {isExpanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                          <div>
                            <CardTitle className="text-lg">{event.event_titre}</CardTitle>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                              <span className="flex items-center gap-1">
                                <CalendarDays className="w-4 h-4" />
                                {formatDate(event.event_date_debut)}
                                {event.event_date_fin && event.event_date_fin !== event.event_date_debut && (
                                  <> - {formatDate(event.event_date_fin)}</>
                                )}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {event.participants.length} participant(s)
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {pendingCount > 0 && (
                            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">
                              {pendingCount} en attente
                            </Badge>
                          )}
                          <div className="text-right">
                            <div className="text-lg font-bold text-primary">{totalEvent.toFixed(0)}€</div>
                            <div className="text-xs text-muted-foreground">Total</div>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-medium text-muted-foreground">Food Truck</th>
                              <th className="px-4 py-2 text-left text-sm font-medium text-muted-foreground">Date participation</th>
                              <th className="px-4 py-2 text-center text-sm font-medium text-muted-foreground">Montant</th>
                              <th className="px-4 py-2 text-center text-sm font-medium text-muted-foreground">Statut</th>
                              <th className="px-4 py-2 text-center text-sm font-medium text-muted-foreground">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {event.participants.map((p) => (
                              <tr key={p.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <Truck className="w-4 h-4 text-muted-foreground" />
                                    <span className="font-medium">{p.food_truck_nom}</span>
                                    {p.is_proposeur && (
                                      <Badge variant="secondary" className="text-xs">Proposeur</Badge>
                                    )}
                                    {p.is_bureau_member && (
                                      <Badge variant="secondary" className="text-xs">Bureau</Badge>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-muted-foreground">
                                  {p.date_participation ? formatDate(p.date_participation) : "-"}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className="font-bold text-primary">{p.montant_du}€</span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {getStatusBadge(p.statut_paiement)}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex justify-center gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setEditDialog({ 
                                        open: true, 
                                        participant: p, 
                                        newAmount: p.montant_du?.toString() || "20",
                                        newStatus: p.statut_paiement || "en_attente"
                                      })}
                                    >
                                      Modifier
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => setDeleteDialog({ open: true, participant: p })}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, participant: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Supprimer cette commission ?
            </DialogTitle>
            <DialogDescription>
              Cette action supprimera définitivement la commission de <strong>{deleteDialog.participant?.food_truck_nom}</strong>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, participant: null })}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, participant: null, newAmount: "", newStatus: "" })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier la commission</DialogTitle>
            <DialogDescription>
              Food Truck: <strong>{editDialog.participant?.food_truck_nom}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Montant (€)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={editDialog.newAmount}
                onChange={(e) => setEditDialog(prev => ({ ...prev, newAmount: e.target.value }))}
                placeholder="20"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Statut de paiement</Label>
              <Select 
                value={editDialog.newStatus} 
                onValueChange={(value) => setEditDialog(prev => ({ ...prev, newStatus: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en_attente">En attente</SelectItem>
                  <SelectItem value="paye">Payé</SelectItem>
                  <SelectItem value="exempte">Exempté</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditDialog({ open: false, participant: null, newAmount: "", newStatus: "" })}>
              Annuler
            </Button>
            <Button onClick={handleUpdateAmount} disabled={!editDialog.newAmount}>
              Mettre à jour le montant
            </Button>
            <Button onClick={handleUpdateStatus} disabled={!editDialog.newStatus}>
              Mettre à jour le statut
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
